import { h, Fragment } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { useSearchProvider } from '../search/useSearchProvider';
import { Layout } from '../components/layout/Layout';
import { FilterPanel } from '../components/filters/FilterPanel';
import { SortDropdown } from '../components/sorting/SortDropdown';
import { ProductGrid } from '../components/product/ProductGrid';
import { InfiniteScroll } from '../components/infinite-scroll/InfiniteScroll';
import { ActiveFilters } from '../components/filters/ActiveFilters';
import { MobileFilterDrawer } from '../components/filters/MobileFilterDrawer';
import { defaultDemoConfig } from '../config/demoConfig';
import { ConfigManager } from '../utils/configManager';
import { getSearchspringDomPageType } from '../platforms/searchspring/domContext';
import styles from './Pages.module.scss';
export function MegaOfficeSuppliesPage() {
    // Load config with environment variable priority
    const [config, setConfig] = useState(() => {
        const urlConfig = ConfigManager.getConfigFromUrl();
        if (urlConfig) {
            // URL config takes highest precedence
            return Object.assign(Object.assign({}, defaultDemoConfig), urlConfig);
        }
        // Use the new method that respects env var priority
        return ConfigManager.loadConfigWithEnvPriority();
    });
    const [controlPanelOpen, setControlPanelOpen] = useState(false);
    // Layout state managed at demo level
    const [layout, setLayout] = useState({
        cardLayout: config.layout.cardLayout,
        filterLayout: config.layout.filterLayout
    });
    // Infinite scroll state
    const [accumulatedProducts, setAccumulatedProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    // Save config when it changes
    useEffect(() => {
        ConfigManager.saveConfig(config);
    }, [config]);
    const [openFilterDrawer, setOpenFilterDrawer] = useState(true);
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
    // Handle config change
    const handleConfigChange = (newConfig) => {
        setConfig(newConfig);
        // Re-run search if provider or mock platform changes
        if (newConfig.search.provider !== config.search.provider ||
            (newConfig.search.provider === 'mock' && newConfig.search.mockPlatform !== config.search.mockPlatform)) {
            window.location.reload(); // Reload to reinitialize provider
        }
    };
    const { products, filters, meta, sortOptions, loading, query, currentSort, appliedFilters, handleQuerySubmit: originalHandleQuerySubmit, handleFilterChange: originalHandleFilterChange, handleRemoveFilter: originalHandleRemoveFilter, handleClearAllFilters: originalHandleClearAllFilters, handleSortChange: originalHandleSortChange, runSearch } = useSearchProvider(config.search.provider, config.search.mockPlatform);
    // Wrap handlers to reset infinite scroll state
    const handleQuerySubmit = (newQuery) => {
        setAccumulatedProducts([]);
        setCurrentPage(1);
        originalHandleQuerySubmit(newQuery);
    };
    const handleFilterChange = (filterId, value) => {
        setAccumulatedProducts([]);
        setCurrentPage(1);
        originalHandleFilterChange(filterId, value);
    };
    const handleSortChange = (sortId) => {
        setAccumulatedProducts([]);
        setCurrentPage(1);
        originalHandleSortChange(sortId);
    };
    const handleRemoveFilter = (filterId, value) => {
        setAccumulatedProducts([]);
        setCurrentPage(1);
        originalHandleRemoveFilter(filterId, value);
    };
    const handleClearAllFilters = () => {
        setAccumulatedProducts([]);
        setCurrentPage(1);
        originalHandleClearAllFilters();
    };
    const hasMounted = useRef(false);
    // Update layout based on config
    useEffect(() => {
        setLayout({
            cardLayout: config.layout.cardLayout,
            filterLayout: config.layout.filterLayout
        });
    }, [config.layout.cardLayout, config.layout.filterLayout]);
    // Reset to first page when pagination type changes (skip initial mount)
    useEffect(() => {
        if (!hasMounted.current)
            return;
        setAccumulatedProducts([]);
        setCurrentPage(1);
        if (runSearch) {
            runSearch(query || '', {
                page: 1,
                pageSize: config.pagination.itemsPerPage,
                sort: currentSort || undefined,
                filters: appliedFilters
            });
        }
    }, [config.pagination.type]);
    // Re-run search when items per page changes (skip initial mount)
    useEffect(() => {
        if (!hasMounted.current)
            return;
        setAccumulatedProducts([]);
        setCurrentPage(1);
        runSearch(query, {
            pageSize: config.pagination.itemsPerPage,
            sort: currentSort || undefined,
            filters: appliedFilters
        });
    }, [config.pagination.itemsPerPage]);
    // Mark as mounted after first render
    useEffect(() => {
        hasMounted.current = true;
    }, []);
    const isSearchspringSearchPage = config.search.provider === 'searchspring' &&
        getSearchspringDomPageType() === 'search_page';
    const searchspringBreadcrumbApplied = useRef(false);
    // Theme breadcrumb: set once after the first successful search (not on filter/sort refetches).
    useEffect(() => {
        if (!isSearchspringSearchPage || loading || !meta || searchspringBreadcrumbApplied.current) {
            return;
        }
        const el = document.querySelector('.breadcrumb-search-text');
        if (el) {
            const q = (query || '').trim();
            const displayQ = q.length ? q : '*';
            el.textContent = `Search: ${meta.totalResults} results for '${displayQ}'`;
        }
        searchspringBreadcrumbApplied.current = true;
    }, [isSearchspringSearchPage, loading, meta, query]);
    // Track the last set of products we processed to avoid double-appending
    const lastProcessedProducts = useRef([]);
    // Accumulate products for infinite scroll
    useEffect(() => {
        const isInfiniteScroll = config.pagination.type !== 'none';
        if (isInfiniteScroll && products.length > 0 && products !== lastProcessedProducts.current) {
            lastProcessedProducts.current = products;
            if (currentPage === 1) {
                setAccumulatedProducts(products);
            }
            else {
                setAccumulatedProducts(prev => [...prev, ...products]);
            }
            setLoadingMore(false);
        }
    }, [products]);
    // Load more function for infinite scroll
    const loadMoreProducts = () => {
        if (!runSearch || loadingMore || !meta)
            return;
        const nextPage = currentPage + 1;
        if (nextPage > meta.totalPages)
            return;
        setLoadingMore(true);
        setCurrentPage(nextPage);
        runSearch(query || '', {
            page: nextPage,
            pageSize: config.pagination.itemsPerPage,
            sort: currentSort || undefined,
            filters: appliedFilters
        });
    };
    // Load less: trim the last page of accumulated products
    const loadLessProducts = () => {
        if (currentPage <= 1)
            return;
        const newPage = currentPage - 1;
        setCurrentPage(newPage);
        setAccumulatedProducts(prev => prev.slice(0, newPage * config.pagination.itemsPerPage));
    };
    // Helper to get product count display
    const getProductCountDisplay = () => {
        if (!meta)
            return '';
        return `${meta.totalResults} results`;
    };
    const searchResultsHeadingQuery = (query || '').trim() || '*';
    // Render main content based on pagination type
    const renderMainContent = () => {
        // Force re-render by using key prop
        const contentKey = `${config.pagination.type}-${layout.cardLayout}-${layout.filterLayout}`;
        switch (config.pagination.type) {
            case 'none':
                return h(ProductGrid, { key: contentKey, products: products, loading: loading, layout: layout, filtersOpen: openFilterDrawer });
            case 'infiniteAuto':
                return (h(InfiniteScroll, { key: contentKey, products: accumulatedProducts, loading: loading || loadingMore, layout: layout, mode: "auto", pageSize: config.pagination.itemsPerPage, hasMore: meta ? currentPage < meta.totalPages : false, onLoadMore: loadMoreProducts, filtersOpen: openFilterDrawer }));
            // 'infiniteButton' + any legacy/cached type (e.g. 'pagination') → Load More button
            default:
                return (h(InfiniteScroll, { key: contentKey, products: accumulatedProducts, loading: loading || loadingMore, layout: layout, mode: "button", pageSize: config.pagination.itemsPerPage, hasMore: meta ? currentPage < meta.totalPages : false, onLoadMore: loadMoreProducts, canLoadLess: currentPage > 1, onLoadLess: loadLessProducts, totalCount: meta === null || meta === void 0 ? void 0 : meta.totalResults, filtersOpen: openFilterDrawer }));
        }
    };
    // Simple layout without Layout component
    const renderSimpleLayout = () => (h("div", { class: "demo-page" },
        config.features.searchBar && (h("div", { class: "search-bar" },
            h("input", { type: "text", value: query, onChange: (e) => handleQuerySubmit(e.currentTarget.value), placeholder: "Search products..." }),
            h("button", { onClick: () => handleQuerySubmit(query) }, "Search"))),
        config.features.productCount && meta && (h("div", { class: styles.productCount }, getProductCountDisplay())),
        renderMainContent()));
    // Complex layout with Layout component
    const renderComplexLayout = () => (h("div", { class: "demo-page" },
        isSearchspringSearchPage && (h("div", { class: styles.searchspringSearchTitle }, `Search results: ${searchResultsHeadingQuery}`)),
        config.features.filters && (h("button", { class: styles.mobileFilterBtn, type: "button", onClick: () => setMobileFilterOpen(true) },
            h("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                h("path", { d: "M8.75 10C8.75 10.2125 8.67808 10.3906 8.53425 10.5342C8.39042 10.6781 8.21225 10.75 7.99975 10.75C7.78708 10.75 7.609 10.6781 7.4655 10.5342C7.32183 10.3906 7.25 10.2125 7.25 10V7.75H4C3.7875 7.75 3.60942 7.67808 3.46575 7.53425C3.32192 7.39042 3.25 7.21225 3.25 6.99975C3.25 6.78708 3.32192 6.609 3.46575 6.4655C3.60942 6.32183 3.7875 6.25 4 6.25H7.25V4C7.25 3.7875 7.32192 3.60942 7.46575 3.46575C7.60958 3.32192 7.78775 3.25 8.00025 3.25C8.21292 3.25 8.391 3.32192 8.5345 3.46575C8.67817 3.60942 8.75 3.7875 8.75 4V10ZM20.75 7.00025C20.75 7.21292 20.6781 7.391 20.5343 7.5345C20.3906 7.67817 20.2125 7.75 20 7.75L12 7.75C11.7875 7.75 11.6094 7.67808 11.4658 7.53425C11.3219 7.39042 11.25 7.21225 11.25 6.99975C11.25 6.78708 11.3219 6.609 11.4658 6.4655C11.6094 6.32183 11.7875 6.25 12 6.25L20 6.25C20.2125 6.25 20.3906 6.32192 20.5343 6.46575C20.6781 6.60958 20.75 6.78775 20.75 7.00025ZM20.75 17.0003C20.75 17.2129 20.6781 17.391 20.5343 17.5345C20.3906 17.6782 20.2125 17.75 20 17.75H16.75V20C16.75 20.2125 16.6781 20.3906 16.5343 20.5342C16.3904 20.6781 16.2122 20.75 15.9998 20.75C15.7871 20.75 15.609 20.6781 15.4655 20.5342C15.3218 20.3906 15.25 20.2125 15.25 20V14C15.25 13.7875 15.3219 13.6094 15.4658 13.4658C15.6096 13.3219 15.7878 13.25 16.0003 13.25C16.2129 13.25 16.391 13.3219 16.5345 13.4658C16.6782 13.6094 16.75 13.7875 16.75 14V16.25H20C20.2125 16.25 20.3906 16.3219 20.5343 16.4658C20.6781 16.6096 20.75 16.7878 20.75 17.0003ZM12.75 17.0003C12.75 17.2129 12.6781 17.391 12.5343 17.5345C12.3906 17.6782 12.2125 17.75 12 17.75H4C3.7875 17.75 3.60942 17.6781 3.46575 17.5342C3.32192 17.3904 3.25 17.2122 3.25 16.9997C3.25 16.7871 3.32192 16.609 3.46575 16.4655C3.60942 16.3218 3.7875 16.25 4 16.25H12C12.2125 16.25 12.3906 16.3219 12.5343 16.4658C12.6781 16.6096 12.75 16.7878 12.75 17.0003Z", fill: "white" })),
            "Filter & Sort")),
        (config.features.sorting || config.features.filters || config.features.productCount) && (h("div", { class: isSearchspringSearchPage
                ? `${styles.productsHeader} ${styles.productsHeaderAfterSearchTitle}`
                : styles.productsHeader },
            h("div", { class: styles.headerLeft },
                config.features.filters && (h("button", { class: styles.filtersButton, type: "button", onClick: layout.filterLayout !== 'filterTop' ? () => setOpenFilterDrawer(!openFilterDrawer) : undefined, style: layout.filterLayout === 'filterTop' ? 'cursor: default' : '' },
                    h("span", { class: styles.filtersIcon },
                        h("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                            h("path", { d: "M8.75 10C8.75 10.2125 8.67808 10.3906 8.53425 10.5342C8.39042 10.6781 8.21225 10.75 7.99975 10.75C7.78708 10.75 7.609 10.6781 7.4655 10.5342C7.32183 10.3906 7.25 10.2125 7.25 10V7.75H4C3.7875 7.75 3.60942 7.67808 3.46575 7.53425C3.32192 7.39042 3.25 7.21225 3.25 6.99975C3.25 6.78708 3.32192 6.609 3.46575 6.4655C3.60942 6.32183 3.7875 6.25 4 6.25H7.25V4C7.25 3.7875 7.32192 3.60942 7.46575 3.46575C7.60958 3.32192 7.78775 3.25 8.00025 3.25C8.21292 3.25 8.391 3.32192 8.5345 3.46575C8.67817 3.60942 8.75 3.7875 8.75 4V10ZM20.75 7.00025C20.75 7.21292 20.6781 7.391 20.5343 7.5345C20.3906 7.67817 20.2125 7.75 20 7.75L12 7.75C11.7875 7.75 11.6094 7.67808 11.4658 7.53425C11.3219 7.39042 11.25 7.21225 11.25 6.99975C11.25 6.78708 11.3219 6.609 11.4658 6.4655C11.6094 6.32183 11.7875 6.25 12 6.25L20 6.25C20.2125 6.25 20.3906 6.32192 20.5343 6.46575C20.6781 6.60958 20.75 6.78775 20.75 7.00025ZM20.75 17.0003C20.75 17.2129 20.6781 17.391 20.5343 17.5345C20.3906 17.6782 20.2125 17.75 20 17.75H16.75V20C16.75 20.2125 16.6781 20.3906 16.5343 20.5342C16.3904 20.6781 16.2122 20.75 15.9998 20.75C15.7871 20.75 15.609 20.6781 15.4655 20.5342C15.3218 20.3906 15.25 20.2125 15.25 20V14C15.25 13.7875 15.3219 13.6094 15.4658 13.4658C15.6096 13.3219 15.7878 13.25 16.0003 13.25C16.2129 13.25 16.391 13.3219 16.5345 13.4658C16.6782 13.6094 16.75 13.7875 16.75 14V16.25H20C20.2125 16.25 20.3906 16.3219 20.5343 16.4658C20.6781 16.6096 20.75 16.7878 20.75 17.0003ZM12.75 17.0003C12.75 17.2129 12.6781 17.391 12.5343 17.5345C12.3906 17.6782 12.2125 17.75 12 17.75H4C3.7875 17.75 3.60942 17.6781 3.46575 17.5342C3.32192 17.3904 3.25 17.2122 3.25 16.9997C3.25 16.7871 3.32192 16.609 3.46575 16.4655C3.60942 16.3218 3.7875 16.25 4 16.25H12C12.2125 16.25 12.3906 16.3219 12.5343 16.4658C12.6781 16.6096 12.75 16.7878 12.75 17.0003Z", fill: "black" }))),
                    layout.filterLayout !== 'filterTop' && h("span", null, "Filters"))),
                config.features.filters && layout.filterLayout === 'filterTop' && (h(FilterPanel, { layout: layout.filterLayout, filters: filters, onFilterChange: handleFilterChange, openFilterDrawer: openFilterDrawer, setOpenFilterDrawer: setOpenFilterDrawer })),
                config.features.productCount && meta && (h("div", { class: styles.productCount }, getProductCountDisplay()))),
            config.features.sorting && (h(SortDropdown, { options: sortOptions, currentSort: currentSort, onChange: handleSortChange })))),
        h(Layout, { key: `layout-${layout.filterLayout}-${layout.cardLayout}`, layout: layout, openFilterDrawer: openFilterDrawer, setOpenFilterDrawer: setOpenFilterDrawer, sidebar: config.features.filters && layout.filterLayout !== 'filterTop' ? (h(FilterPanel, { layout: layout.filterLayout, filters: filters, onFilterChange: handleFilterChange, openFilterDrawer: openFilterDrawer, setOpenFilterDrawer: setOpenFilterDrawer })) : null, main: h(Fragment, null,
                h(ActiveFilters, { appliedFilters: appliedFilters, filters: filters, onRemove: handleRemoveFilter, onClearAll: handleClearAllFilters }),
                renderMainContent()) }),
        config.pagination.type !== 'none' && config.pagination.type !== 'infiniteAuto' && (h("div", { class: styles.paginationBar },
            h("div", { class: styles.paginationControls }, meta && currentPage < meta.totalPages && (h("button", { class: styles.loadMoreBtn, type: "button", onClick: loadMoreProducts, disabled: loadingMore }, loadingMore ? 'Loading...' : 'Load More'))),
            meta && (h("p", { class: styles.paginationProgress },
                accumulatedProducts.length,
                " / ",
                meta.totalResults)))),
        h(MobileFilterDrawer, { isOpen: mobileFilterOpen, onClose: () => setMobileFilterOpen(false), filters: filters, onFilterChange: handleFilterChange, sortOptions: sortOptions, currentSort: currentSort, onSortChange: handleSortChange, onClearAll: handleClearAllFilters, meta: meta })));
    // Determine which layout to use
    const shouldUseComplexLayout = config.features.filters || config.features.sorting;
    return (h(Fragment, null, shouldUseComplexLayout ? renderComplexLayout() : renderSimpleLayout()));
}
